import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useReselector, useScrollRef } from '@/utils/sharedHooks'
import {
  selectFinancialAccounts,
  selectPlaidItems,
} from '@/selectors/financeSelectors'
import { getCurrentUser } from '@/selectors/user.selectors'
import { updateFinancialProfile } from '@/actions/financialProfileActions'
import { postCompleteOnboarding } from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { useAppDispatch } from '@/utils/typeHelpers'
import { OnboardingScreen } from '@/features/onboarding/OnboardingScreen'
import { PrimaryUseAction, PrimaryUseAnswer, PrimaryUseState } from './types'
import {
  isPrimaryUseAnswerComplete,
  seedPrimaryUseAnswer,
} from './primaryUseAnswers'
import { FinancialAccountPrimaryUse } from '@/reducers/finances/financialAccountsReducer'
import { buildDesignationUpdate } from '@/features/onboarding/accounts-stage/designation'
import {
  FETCH_FINANCIAL_ACCOUNT_KEY,
  fetchFinancialAccountsIfNeeded,
  UPDATE_PLAID_ITEM_KEY,
  updateFinancialAccount,
} from '@/actions/financialAccountActions'
import { updateUser } from '@/actions/userActions'
import {
  Accordion,
  Alert,
  Banner,
  BannerVariants,
  Image,
  List,
  ListCustomBullets,
  Text,
  Card,
  Button,
  Dialog,
} from '@/ui'
import { TypographyStyles } from '@/styles'
import { faLink, faPlus } from '@fortawesome/pro-regular-svg-icons'
import {
  faArrowUpArrowDown,
  faFileArrowUp,
} from '@fortawesome/pro-solid-svg-icons'
import { PlaidLink } from '@/components/plaid/PlaidLink'
import {
  FETCH_PLAID_ITEM_KEY,
  fetchPlaidItemsIfNeeded,
} from '@/actions/plaidItemActions'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/flags'
import { useSyncStatus } from '@/components/plaid/helpers'
import {
  FiTier,
  parseInstitutionSelections,
  type SelectedInstitution,
} from '@/features/user/signup/about-you/bank-compatibility.types'
import {
  LoadingOnboardingInstitutionCard,
  OnboardingInstitutionCard,
} from '@/features/user/settings/sections/connected-financial-institutions-card/institution-card/OnboardingInstitutionCard'
import { selectIsFetchingForKeys } from '@/reducers/fetch'
import {
  SyncErrorClassification,
  FETCH_PLAID_ACCESS_TOKEN_KEY,
} from '@/actions/plaid/authActions'
import { fetchBankingProfile } from '@/features/banking/bankingSlice'
import { selectBankingApplicationStatus } from '@/features/banking/banking.selectors'
import { BankingApplicationStatus } from '@/features/banking/banking.types'
import {
  OFFER_SOURCES,
  useBankingAnalytics,
} from '@/features/banking/analytics'
import { isManualAccount } from '@/features/user/settings/sections/self-service/financial-institutions/bankAccounts.helpers'

export interface ConnectStageProps {
  /** Called when the user clicks "Back" */
  onBack: () => void
}

export const ConnectStage = ({ onBack }: ConnectStageProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const plaidInstitutions = useReselector(selectPlaidItems)
  const financialAccounts = useReselector(selectFinancialAccounts)
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const user = useReselector(getCurrentUser)
  const cantFindBank = user?.financialProfile?.hasIncompatibleAccounts

  const isFetching = useReselector(selectIsFetchingForKeys, [
    FETCH_FINANCIAL_ACCOUNT_KEY,
    FETCH_PLAID_ITEM_KEY,
    UPDATE_PLAID_ITEM_KEY,
    FETCH_PLAID_ACCESS_TOKEN_KEY,
  ])

  const [institutionSelections] = useState<SelectedInstitution[] | null>(() =>
    parseInstitutionSelections(
      sessionStorage.getItem('signupInstitutionSelections')
    )
  )

  const hasUnsupportedBank = useMemo(
    () =>
      institutionSelections?.some((i) => i.tier === FiTier.NOT_SUPPORTED) ??
      false,
    [institutionSelections]
  )
  const [showAtLeastOneAccountAlert, setShowAtLeastOneAccountAlert] =
    useState(false)
  const [showMissingAnswerAlert, setShowMissingAnswerAlert] = useState(false)
  const [showCantFindBankDialog, setShowCantFindBankDialog] = useState(false)

  const presortEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.presortEnabled,
    false
  )

  const bankingStatus = useReselector(selectBankingApplicationStatus)
  const { trackOfferViewed } = useBankingAnalytics()

  useEffect(() => {
    dispatch(fetchBankingProfile())
  }, [dispatch])

  // Fire the offer-view event when the "Can't find my bank" dialog is open.
  useEffect(() => {
    if (showCantFindBankDialog) {
      trackOfferViewed(OFFER_SOURCES.OnboardingCantFindBankModal)
    }
  }, [showCantFindBankDialog, trackOfferViewed])

  const { plaidItems: plaidSyncStatuses, refetch } = useSyncStatus()

  // Refetch when plaid institutions change
  useEffect(() => {
    refetch()
  }, [plaidInstitutions, refetch])

  const [showTransientErrorAlert, setShowTransientErrorAlert] = useState(false)
  const { scrollRef: topOfScreenRef, scrollToRef: scrollToTop } = useScrollRef()

  const hasTransientError = plaidSyncStatuses.some(
    (s) =>
      s.syncErrorClassification === SyncErrorClassification.TRANSIENT_RETRYABLE
  )

  const [primaryUseAnswers, setPrimaryUseAnswer] = useReducer(
    (
      state: PrimaryUseState,
      {
        institutionId,
        financialAccountId,
        primaryUse,
        hasOtherType,
      }: PrimaryUseAction
    ) => {
      const current = state[institutionId]?.[financialAccountId]

      // Toggle fires on every tap, so re-tapping the current selection must not
      // discard an answered follow-up.
      if (primaryUse !== undefined && primaryUse === current?.primaryUse) {
        return state
      }

      // Both uses ask a follow-up, so switching either way reopens it unanswered
      // rather than inheriting the answer given for the opposite direction.
      const next: PrimaryUseAnswer =
        primaryUse === undefined
          ? {
              primaryUse: current?.primaryUse ?? null,
              hasOtherType: hasOtherType ?? null,
            }
          : { primaryUse, hasOtherType: null }

      return {
        ...state,
        [institutionId]: {
          ...(state[institutionId] || {}),
          [financialAccountId]: next,
        },
      }
    },
    {}
  )

  // Event names match feature/core-7166's account-type funnel so both surfaces
  // report as one series. Fired per tap, not on save, so selection changes and
  // abandonment before Continue stay visible.
  const handleSelectPrimaryUse = useCallback(
    (
      institutionId: string,
      financialAccountId: number,
      primaryUse: FinancialAccountPrimaryUse
    ) => {
      track('onboarding_account_type_select_primary_use', {
        financial_account_id: financialAccountId,
        primary_use: primaryUse,
      })
      setPrimaryUseAnswer({ institutionId, financialAccountId, primaryUse })
    },
    [track]
  )

  const handleSelectHasOtherType = useCallback(
    (
      institutionId: string,
      financialAccountId: number,
      hasOtherType: boolean
    ) => {
      const primaryUse =
        primaryUseAnswers[institutionId]?.[financialAccountId]?.primaryUse
      const isBusinessAccount =
        primaryUse === FinancialAccountPrimaryUse.BUSINESS

      track('onboarding_account_type_select_has_other_type', {
        financial_account_id: financialAccountId,
        primary_use: primaryUse,
        ...(isBusinessAccount
          ? { has_personal_charges: hasOtherType }
          : { has_business_charges: hasOtherType }),
      })
      setPrimaryUseAnswer({ institutionId, financialAccountId, hasOtherType })
    },
    [track, primaryUseAnswers]
  )

  // An untouched account has no answer at all, so the gate walks accounts rather than
  // answers. Two sources hold them, merged by id:
  //
  // - the financialAccounts store, which the card renders from, and which wins wherever it
  //   has the row. Nothing dispatches receiveUpdatePlaidItemFinancialAccount, so a row
  //   deactivated via settings is inactive here while its embedded copy stays stale-active;
  //   trusting the embedded copy would block Continue forever on a row the card hides.
  // - the plaid item's own accounts array, which lands from a different fetch and arrives
  //   first. It fills in only rows the store hasn't got yet; without it, a Continue during
  //   that window falls through and completes onboarding with nothing persisted.
  //
  // Inactive is dropped after the merge, since the card filters it out and an unreachable
  // row must never block.
  const hasMissingAnswer = useMemo(() => {
    return Object.values(plaidInstitutions).some(
      ({ id, institutionId, accounts }) => {
        const storeAccounts = Object.values(financialAccounts).filter(
          (account) => account.plaidItemId === id
        )
        const storeAccountIds = new Set(
          storeAccounts.map((account) => account.id)
        )

        return [
          ...storeAccounts,
          ...(accounts ?? []).filter(
            (account) => !storeAccountIds.has(account.id)
          ),
        ]
          .filter((account) => !account.inactive)
          .some(
            (account) =>
              !isPrimaryUseAnswerComplete(
                primaryUseAnswers[institutionId]?.[account.id]
              )
          )
      }
    )
  }, [plaidInstitutions, financialAccounts, primaryUseAnswers])

  useEffect(() => {
    if (!hasMissingAnswer) {
      setShowMissingAnswerAlert(false)
    }
  }, [hasMissingAnswer])

  const numberOfLinkedAccounts = Object.keys(plaidInstitutions).length
  const hasManualAccount = Object.values(financialAccounts).some(
    (account) => isManualAccount(account) && !account.inactive
  )

  useEffect(() => {
    pageView('onboarding items connect financial accounts')
  }, [pageView])

  useEffect(() => {
    dispatch(fetchFinancialAccountsIfNeeded())
    dispatch(fetchPlaidItemsIfNeeded())
  }, [dispatch])

  useEffect(() => {
    Object.values(plaidInstitutions).forEach(({ accounts, institutionId }) => {
      accounts.forEach((account) => {
        // primaryUseSetAt is what makes the follow-up bools real answers rather
        // than the column default, so only seed the follow-up once set. Inactive
        // accounts are skipped — the card doesn't render them, so a seeded answer
        // would gate Continue on a row the user can't reach.
        if (account.primaryUse && !account.inactive) {
          setPrimaryUseAnswer({
            institutionId,
            financialAccountId: account.id,
            primaryUse: account.primaryUse,
          })
          const { hasOtherType: seededFollowUp } = seedPrimaryUseAnswer(account)

          if (seededFollowUp !== null) {
            setPrimaryUseAnswer({
              institutionId,
              financialAccountId: account.id,
              hasOtherType: seededFollowUp,
            })
          }
        }
      })
    })
  }, [plaidInstitutions])

  const handleSaveIncompatibleAccounts = useCallback(async () => {
    await updateFinancialProfile(user?.financialProfile?.id, {
      hasIncompatibleAccounts: cantFindBank,
    })(dispatch)
  }, [dispatch, cantFindBank, user?.financialProfile?.id])

  const handleLinkComplete = useCallback(async () => {
    await handleSaveIncompatibleAccounts()

    await Promise.all(
      Object.values(primaryUseAnswers)
        .flatMap((accounts) => Object.entries(accounts))
        .filter(([, answer]) => answer.primaryUse)
        .map(([financialAccountId, answer]) => {
          const isBusinessAccount =
            answer.primaryUse === FinancialAccountPrimaryUse.BUSINESS
          // hasOtherType means personal charges on a business account and
          // business charges on a personal one.
          const answeredFollowUp = answer.hasOtherType ?? undefined
          const followUp = isBusinessAccount
            ? { hasPersonalTransactions: answeredFollowUp }
            : { hasBusinessTransactions: answeredFollowUp }

          return updateFinancialAccount(parseInt(financialAccountId), {
            onboardingAnswers: {
              isBusinessAccount,
              ...(answer.hasOtherType === null ? {} : followUp),
            },
            ...buildDesignationUpdate({
              isBusinessAccount,
              ...followUp,
            }),
          })(dispatch)
        })
    )

    track('completed connect financial accounts', { numberOfLinkedAccounts })

    const nextStep = presortEnabled
      ? '/onboarding-v2?stage=transaction-review'
      : '/dashboard'

    // With separation on the member still has sorting ahead of them, so the completion
    // stamp belongs at the end of the books question rather than here.
    if (!presortEnabled) {
      await dispatch(postCompleteOnboarding())
    }
    await updateUser(user?.id, {
      lastOnboardingScreen: nextStep,
    })(dispatch)

    sessionStorage.removeItem('signupInstitutionSelections')
    navigate(nextStep)
  }, [
    handleSaveIncompatibleAccounts,
    primaryUseAnswers,
    presortEnabled,
    track,
    numberOfLinkedAccounts,
    dispatch,
    user?.id,
    navigate,
  ])

  // Hide alert once user adds accounts
  useEffect(() => {
    if (numberOfLinkedAccounts || hasManualAccount) {
      setShowAtLeastOneAccountAlert(false)
    }
  }, [numberOfLinkedAccounts, hasManualAccount])

  const [clickedTroubleConnecting, setClickedTroubleConnecting] =
    useState(false)

  const handleTroubleConnecting = useCallback(() => {
    if (!clickedTroubleConnecting) {
      track('clicked accordion open', {
        screen: 'connect financial accounts',
        title: 'trouble connecting account',
      })
      setClickedTroubleConnecting(true)
    }
  }, [clickedTroubleConnecting, track])

  const isApprovedHeardBanking =
    bankingStatus === BankingApplicationStatus.Approved
  const isHeardBankingApplicationUnderReview =
    bankingStatus === BankingApplicationStatus.PendingReview ||
    bankingStatus === BankingApplicationStatus.AwaitingDocuments
  const isSkippedOrDeniedHeardBanking =
    bankingStatus === BankingApplicationStatus.Denied ||
    bankingStatus === BankingApplicationStatus.Canceled ||
    bankingStatus === null

  const handleContinue = () => {
    // A pending Heard Banking application can't be connected yet, and a bank
    // Plaid can't reach is added by hand, so neither needs a Plaid connection.
    if (
      numberOfLinkedAccounts === 0 &&
      !hasManualAccount &&
      !isHeardBankingApplicationUnderReview
    ) {
      setShowAtLeastOneAccountAlert(true)
      scrollToTop()
      return
    }

    if (hasTransientError) {
      setShowTransientErrorAlert(true)
      scrollToTop()
      return
    }

    if (hasMissingAnswer) {
      setShowMissingAnswerAlert(true)
      scrollToTop()
      return
    }

    track('completed onboarding item connect financial accounts', {
      status: 'done',
    })
    handleLinkComplete()
  }

  const handleBack = () => {
    handleSaveIncompatibleAccounts()
    onBack()
  }

  return (
    <OnboardingScreen onBackClick={handleBack} onContinueClick={handleContinue}>
      <span ref={topOfScreenRef} />
      {showAtLeastOneAccountAlert && (
        <Alert
          variant="error"
          title="Connect at least one account"
          description="Heard requires at least one compatible financial account connection to work. "
          className="mb-10"
          dismissible
          onClose={() => setShowAtLeastOneAccountAlert(false)}
        />
      )}
      {showMissingAnswerAlert && (
        <Alert
          variant="error"
          title="Tell us how each account is used"
          description="For every account below, choose Business or Personal, then answer whether it also has charges of the other type."
          className="mb-10"
        />
      )}
      {showTransientErrorAlert && (
        <Alert
          variant="error"
          title="We're having trouble connecting your account"
          description="Check your bank connection below to retry connection."
          className="mb-10"
          dismissible
          onClose={() => setShowTransientErrorAlert(false)}
        />
      )}
      {hasUnsupportedBank && (
        <Alert
          variant="error"
          title="Some of your banks are not supported by Plaid"
          description="You'll need to upload statements manually for unsupported banks. Consider switching to a supported bank for the full Heard experience."
          className="mb-10"
        />
      )}
      <div className="flex flex-col self-center">
        <Text
          variant={TypographyStyles.DISPLAY_MD}
          className="text-neutral-900 mt-4"
        >
          Connect bank accounts
        </Text>
        <Text variant={TypographyStyles.BODY_SM} className="mt-4">
          Connect all bank and credit card accounts used for your practice,
          including existing{' '}
          <b>business accounts and personal accounts with business expenses.</b>
        </Text>
        <Text variant={TypographyStyles.BODY_SM} className="mt-4">
          {presortEnabled ? (
            <>
              We&apos;ll sort business from personal for you, and ask about
              anything we&apos;re unsure of.
            </>
          ) : (
            <>
              If you connect a personal account, we&apos;ll need your help
              separating out your personal transactions later.
            </>
          )}
        </Text>

        {isHeardBankingApplicationUnderReview && (
          <Alert
            variant="default"
            title="Your Heard Banking account is under review"
            description="In the meantime, please connect any other additional accounts that contain business expenses."
            className="mt-6"
          />
        )}

        {showCantFindBankDialog && (
          <Dialog
            onClose={() => setShowCantFindBankDialog(false)}
            open={showCantFindBankDialog}
          >
            <Dialog.Content>
              <Text variant={TypographyStyles.LABEL_MD}>
                Can&apos;t find your bank?
              </Text>
              <Text variant={TypographyStyles.BODY_SM}>
                Your bank is not supported by Plaid, which is how we automate
                your experience. What this means:
              </Text>
              <ListCustomBullets variant="icon" className="pl-14">
                <ListCustomBullets.Item
                  icon={faArrowUpArrowDown}
                  iconBackgroundColor="priority-bold"
                  iconSize="sm"
                  className="items-center"
                >
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    className="text-neutral-600"
                  >
                    Without a dedicated business banking account, you&apos;ll
                    need to separate your personal transactions from your
                    business expenses <b>each month</b>.
                  </Text>
                </ListCustomBullets.Item>
                <ListCustomBullets.Item
                  icon={faFileArrowUp}
                  iconBackgroundColor="priority-bold"
                  iconSize="sm"
                  className="items-center"
                >
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    className="text-neutral-600"
                  >
                    You&apos;ll need to <b>upload past statements</b> for us to
                    accurately reconstruct and reconcile your books.
                  </Text>
                </ListCustomBullets.Item>
                <ListCustomBullets.Item
                  icon={faFileArrowUp}
                  iconBackgroundColor="priority-bold"
                  iconSize="sm"
                  className="items-center"
                >
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    className="text-neutral-600"
                  >
                    You&apos;ll also need to upload statements manually{' '}
                    <b>each month</b> for us to keep your books accurate.
                  </Text>
                </ListCustomBullets.Item>
              </ListCustomBullets>
              <Dialog.Footer>
                <Button
                  onClick={() => setShowCantFindBankDialog(false)}
                  variant="secondary"
                >
                  Close
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog>
        )}

        {Boolean(numberOfLinkedAccounts) && (
          <>
            {Object.values(plaidInstitutions).map((institution) => (
              <OnboardingInstitutionCard
                key={institution.id}
                institution={institution}
                hasOtherAccounts={numberOfLinkedAccounts > 1}
                showPrimaryUseErrors={showMissingAnswerAlert}
                primaryUseAnswers={
                  primaryUseAnswers[institution.institutionId] || {}
                }
                onSelectPrimaryUse={(financialAccountId, primaryUse) =>
                  handleSelectPrimaryUse(
                    institution.institutionId,
                    financialAccountId,
                    primaryUse
                  )
                }
                onSelectHasOtherType={(financialAccountId, hasOtherType) =>
                  handleSelectHasOtherType(
                    institution.institutionId,
                    financialAccountId,
                    hasOtherType
                  )
                }
                syncStatus={
                  plaidSyncStatuses.find(
                    (item) => item.plaidItemId === institution.id
                  ) || null
                }
                restartSync={refetch}
              />
            ))}

            <PlaidLink
              buttonVariant="secondary"
              redirect={false}
              iconLeft={faPlus}
              connectButtonText="Connect another bank"
              className="mt-2"
            />
          </>
        )}

        {!numberOfLinkedAccounts && !isFetching && isApprovedHeardBanking && (
          <Card
            childrenClassName="flex flex-col items-center text-center gap-2"
            className="mt-10"
          >
            <Image
              src="https://heard-images.s3.amazonaws.com/assets/heard-h-green-grad.png"
              alt="Heard Banking"
              width={48}
              height={48}
              className="rounded-full"
            />
            <Text variant={TypographyStyles.LABEL_MD}>Heard Banking</Text>
            <Text
              variant={TypographyStyles.BODY_SM}
              className="text-neutral-600"
            >
              Now that you&apos;ve been approved, let&apos;s start pulling in
              your transactions.
            </Text>
            {/* TODO: iterate to launch Plaid scoped to the Heard-on-Plaid-Exchange institution */}
            <PlaidLink
              redirect={false}
              connectButtonText="Activate Heard Banking"
              className="mt-2"
            />
            <Banner
              variant={BannerVariants.DEFAULT}
              wrapperClassName="w-full mt-2"
            >
              <b>Tip: </b>
              New accounts sometimes take a few minutes to show up in Plaid. If
              you&apos;re having issues, try again in a few minutes.
            </Banner>
          </Card>
        )}

        {!numberOfLinkedAccounts && !isFetching && !isApprovedHeardBanking && (
          <Card
            childrenClassName="flex flex-col items-center"
            className="mt-10"
          >
            <Image
              src="https://heard-images.s3.amazonaws.com/assets/bank-yellow.svg"
              alt="bank"
              width={80}
              height={80}
              objectFit="scaleDown"
            />
            <PlaidLink
              redirect={false}
              iconLeft={faLink}
              connectButtonText="Connect Bank Account"
              className="mt-4"
            />

            <Button
              variant="ghost"
              className="mt-2"
              onClick={() => setShowCantFindBankDialog(true)}
            >
              I can&apos;t find my bank
            </Button>
          </Card>
        )}

        {isFetching && <LoadingOnboardingInstitutionCard />}

        <Text variant={TypographyStyles.H2} className="mt-10">
          Need help?
        </Text>
        <Accordion type="multiple" collapsible variant="text">
          {isSkippedOrDeniedHeardBanking && (
            <Accordion.Item value="business-bank-account">
              <Accordion.Trigger>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  className="text-neutral-900"
                >
                  What if I don&apos;t have a business bank account?
                </Text>
              </Accordion.Trigger>
              <Accordion.Content>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  className="leading-normal"
                >
                  Without a dedicated business banking account, you&apos;ll need
                  to separate your personal transactions from your business
                  expenses each month. For compliance, we highly recommend
                  opening a separate business banking account.
                  <br />
                  <br />
                  Many providers start with:
                  <List bulleted className="pl-8">
                    <List.Item>Bluevine</List.Item>
                    <List.Item>Bank of America</List.Item>
                    <List.Item>Wells Fargo</List.Item>
                    <List.Item>Chase</List.Item>
                    <List.Item>U.S. Bank</List.Item>
                    <List.Item>Relay Financial</List.Item>
                    <List.Item>PNC</List.Item>
                  </List>
                </Text>
              </Accordion.Content>
            </Accordion.Item>
          )}
          <Accordion.Item value="accordion-item-2">
            <Accordion.Trigger onClick={handleTroubleConnecting}>
              <Text
                variant={TypographyStyles.BODY_SM}
                className="text-neutral-900"
              >
                I’m having trouble connecting my account
              </Text>
            </Accordion.Trigger>
            <Accordion.Content>
              If you’re experiencing Plaid errors that prevent you from
              connecting your account, please wait and try again later. If you
              continue to have difficulty, please reach out to support for
              assistance.
            </Accordion.Content>
          </Accordion.Item>
          <Accordion.Item value="accordion-item-3">
            <Accordion.Trigger onClick={handleTroubleConnecting}>
              <Text
                variant={TypographyStyles.BODY_SM}
                className="text-neutral-900"
              >
                What if I have personal transactions in my business account?
              </Text>
            </Accordion.Trigger>
            <Accordion.Content>
              {presortEnabled ? (
                <>
                  That&apos;s common, and we handle it. We sort the
                  account&apos;s transactions into business and personal, and
                  ask you about the ones we can&apos;t tell apart. A dedicated
                  business account still saves everyone time.
                </>
              ) : (
                <>
                  If your business and personal expenses share the same account,
                  check &apos;Has personal.&apos; You&rsquo;ll need to flag any
                  personal transactions each month. We recommend a dedicated
                  business account to save time.
                </>
              )}
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>
      </div>
    </OnboardingScreen>
  )
}
